import { useState } from "react";
import { closeRestaurantNow } from "../services/availabilityService";
import { apiErrorMessage, friendlyDateTime } from "../utils/workingHoursUtils";

interface Props {
  restaurantId: string;
  onClose: () => void;
  onDone: () => void;
}

type Mode = "duration" | "endOfDay" | "indefinite";

const QUICK_DURATIONS = [
  { minutes: 30, label: "30 dk" },
  { minutes: 60, label: "1 saat" },
  { minutes: 120, label: "2 saat" },
  { minutes: 240, label: "4 saat" },
];

const QUICK_REASONS = ["Mutfakta yoğunluk", "Kurye yetersiz", "Teknik arıza", "Malzeme tükendi", "Personel eksik"];

export default function CloseNowModal({ restaurantId, onClose, onDone }: Props) {
  const [mode, setMode] = useState<Mode>("duration");
  const [minutes, setMinutes] = useState(60);
  const [customMinutes, setCustomMinutes] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const usingCustom = customMinutes !== "";
  const effectiveMinutes = usingCustom ? Number(customMinutes) : minutes;
  const customInvalid = usingCustom && (!(effectiveMinutes >= 5) || effectiveMinutes > 1440);

  const reopenPreview = mode === "duration" && !customInvalid
    ? friendlyDateTime(new Date(Date.now() + effectiveMinutes * 60000))
    : mode === "endOfDay" ? "Bu gece 00:00" : null;

  const submit = async () => {
    if (mode === "duration" && customInvalid) { setError("Süre 5 dakika ile 24 saat (1440 dk) arasında olmalı."); return; }
    setSaving(true);
    setError("");
    try {
      await closeRestaurantNow({
        restourantId: restaurantId,
        durationMinutes: mode === "duration" ? effectiveMinutes : undefined,
        untilEndOfDay: mode === "endOfDay",
        indefinitely: mode === "indefinite",
        reason: reason.trim() || undefined,
      });
      onDone();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Restoran kapatılamadı."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="wh-modal">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: "#fef2f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-pause-circle-fill"></i>
            </span>
            Restoranı Şimdi Kapat
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.15rem" }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <span className="wh-label">Ne kadar süre?</span>
            <div className="wh-chips">
              {([["duration", "Belirli süre", "bi-hourglass-split"], ["endOfDay", "Gün sonuna kadar", "bi-moon"], ["indefinite", "Süresiz", "bi-infinity"]] as [Mode, string, string][]).map(([m, label, icon]) => (
                <button key={m} type="button" className={`wh-chip${mode === m ? " wh-chip--active" : ""}`} onClick={() => setMode(m)}>
                  <i className={`bi ${icon}`}></i>{label}
                </button>
              ))}
            </div>
          </div>

          {mode === "duration" && (
            <div>
              <div className="wh-chips">
                {QUICK_DURATIONS.map((d) => (
                  <button key={d.minutes} type="button" className={`wh-chip${!usingCustom && minutes === d.minutes ? " wh-chip--active" : ""}`}
                    onClick={() => { setMinutes(d.minutes); setCustomMinutes(""); }}>
                    {d.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <input type="number" min={5} max={1440} className="wh-input" style={{ maxWidth: 140, borderColor: customInvalid ? "#f87171" : undefined }}
                  placeholder="Özel (dk)" value={customMinutes} onChange={(e) => setCustomMinutes(e.target.value)} />
                <span className="wh-hint" style={{ marginTop: 0 }}>5 dk – 24 saat arası</span>
              </div>
            </div>
          )}

          {mode === "indefinite" ? (
            <div className="wh-note wh-note--warn">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span><b>Kendiliğinden açılmaz.</b> Restoran, biri <b>"Şimdi Aç"</b>a basana kadar sipariş alamaz. Unutulursa günlerce kapalı kalabilir.</span>
            </div>
          ) : reopenPreview && (
            <div className="wh-note wh-note--info">
              <i className="bi bi-alarm"></i>
              <span>Restoran <b>{reopenPreview}</b> itibarıyla haftalık programına kendiliğinden döner.</span>
            </div>
          )}

          <div>
            <span className="wh-label">Sebep <span style={{ fontWeight: 500, color: "#9ca3af" }}>(müşteriye gösterilir, opsiyonel)</span></span>
            <div className="wh-chips" style={{ marginBottom: 8 }}>
              {QUICK_REASONS.map((r) => (
                <button key={r} type="button" className={`wh-chip${reason === r ? " wh-chip--active" : ""}`} onClick={() => setReason(reason === r ? "" : r)}>{r}</button>
              ))}
            </div>
            <input className="wh-input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ya da kendiniz yazın…" />
            <div className="wh-hint" style={{ textAlign: "right" }}>{reason.length}/200</div>
          </div>

          {error && <div className="wh-note wh-note--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button className="wh-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button className="wh-btn wh-btn--danger" onClick={submit} disabled={saving || (mode === "duration" && customInvalid)}>
            {saving ? <><span className="spinner-border spinner-border-sm"></span>Kapatılıyor…</> : <><i className="bi bi-pause-fill"></i>Kapat</>}
          </button>
        </div>
      </div>
    </div>
  );
}
